import React, { useState, useEffect } from 'react';

const PWAInstallButton = ({ className = "" }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      // Check for standalone mode (PWA is installed)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return;
      }
      
      // Check for iOS standalone mode
      if (window.navigator.standalone === true) {
        setIsInstalled(true);
        return;
      }
      
      // Check for Android installed state
      if (document.referrer.includes('android-app://')) {
        setIsInstalled(true);
        return;
      }
    };

    checkIfInstalled();

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      console.log('📱 PWA install prompt available');
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('📱 PWA was installed');
      setIsInstalled(true);
      setShowInstallButton(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // For iOS Safari, show install button if not installed
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInStandaloneMode = window.navigator.standalone;
    
    if (isIOS && !isInStandaloneMode && !isInstalled) {
      setShowInstallButton(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstallClick = async () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      // Show iOS instructions modal
      setShowIOSInstructions(true);
      return;
    }

    if (deferredPrompt) {
      // Show Android/Chrome install prompt
      console.log('📱 Showing install prompt');
      deferredPrompt.prompt();
      
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`📱 User response to install prompt: ${outcome}`);
      
      if (outcome === 'accepted') {
        console.log('📱 User accepted the install prompt');
      } else {
        console.log('📱 User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
      setShowInstallButton(false);
    }
  };

  const closeIOSInstructions = () => {
    setShowIOSInstructions(false);
  };

  // Don't show button if app is already installed
  if (isInstalled || !showInstallButton) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleInstallClick}
        className={`px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-300 border border-white border-opacity-30 shadow-lg hover:shadow-xl transform hover:scale-105 ${className}`}
        aria-label="Install Hairalyzer App"
      >
        📱 Install App
      </button>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                📱 Install Hairalyzer App
              </h3>
              <div className="space-y-4 text-left">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">1️⃣</span>
                  <span className="text-gray-700">Tap the Share button</span>
                  <span className="text-2xl">□↗</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">2️⃣</span>
                  <span className="text-gray-700">Select "Add to Home Screen"</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">3️⃣</span>
                  <span className="text-gray-700">Tap "Add" to install</span>
                </div>
              </div>
              <div className="mt-6 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 The app will appear on your home screen like any other app!
                </p>
              </div>
              <button
                onClick={closeIOSInstructions}
                className="mt-6 px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-colors duration-200"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallButton;
